import React, { useEffect, useState } from 'react';
import { Button, Table, Spin, Row, Col, Progress, Flex, Modal, Tooltip, Select, Input, message } from 'antd';
import { DownloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, RadarChartOutlined, ClearOutlined, ProfileOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { getIppta, deleteIppta } from '../../services/ippta/get_ippta';
import { selectAppPermissions } from '../../store/auth/selectors';
import { useSelector } from 'react-redux';
import IpptaForm from './IpptaForm';
import IpptaRadar from './IpptaRadar';
import { getProyectos } from '../../services/ippta/get_dataIppta';
import IpptaExport from './IpptaExport';
import { getIpptaExportable } from '../../services/ippta/get_ippta';
import PlanAccion from '../Ippta/PlanAccion';
import { useParams } from "react-router-dom";
import FileManagerPOA from '../Poa/FileManagerPOA.jsx';


const twoColors = { '0%': '#108ee9', '100%': '#87d068', };
const { Option } = Select;

const Ippta = ({ proyectoCodigo }) => {
  const { id } = useParams();
  const proyectoFiltrar = proyectoCodigo || id || "";

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setStatus] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showRadar, setShowRadar] = useState(false);
  const [formData, setFormData] = useState(null);
  const [identificacionGrafica, setIdentificacionGrafica] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [dataExport, setDataExport] = useState(null);
  const [originalData, setOriginalData] = useState([]);
  const [identificacionFilter, setIdentificacionFilter] = useState('');
  const [proyectoFilter, setProyectoFilter] = useState('');
  const [proyectos, setProyectos] = useState([]);
  const [oidRegistro, setOidRegistro] = useState(null);
  const [showPlanAccion, setShowPlanAccion] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const appName = 'sistematizacionIppta';
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const user = useSelector((state) => state.auth.user);
  const userLogin = user.username;
  const permissionInsert = permissions.priv_insert === 'Y';
  const permissionExport = permissions.priv_export === 'Y';
  const permissionDelete = permissions.priv_delete === 'Y';
  const [isExporting, setIsExporting] = useState(false);
  const [itemParams, setItemParams] = useState(null);
  const [openModalVisitasEcas, setOpenModalVisitasEcas] = useState(false);

  const handleEdit = (record) => {
    setFormData(record);
    setIsModalVisible(true);
  };

  const handleChart = (record) => {
    setIdentificacionGrafica(record.identificacion);
    setShowRadar(true);
  };

  const handPlanAccion = (record) => {
    setOidRegistro(record.codigo_ippta);
    setShowPlanAccion(true);
  }

  const handleCloseModalPlanAccion = () => {
    setShowPlanAccion(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const proyectosData = await getProyectos();
      setProyectos(proyectosData.data || []);

      const result = await getIppta();
      const combinedData = result.data.map(item => ({
        ...item,
        codigoProyecto: `${item.codigo} - ${item.proyecto}`,
        medicionGeneral: `${Math.round(
          (
            (item.productiva / 35) +
            (item.socioeconomico / 25) +
            (item.ambiental / 30)
          ) / 3 * 100
        )}%`
      }));
      setDataSource(combinedData);
      setOriginalData(combinedData);
      setStatus(null);
    } catch (error) {
      setStatus(error.response ? error.response.status : 500);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (proyectoFiltrar) {
      setProyectoFilter(proyectoFiltrar);
    }
  }, [proyectoFiltrar]);


  useEffect(() => {
    const fetchData = async () => {
      setIsExporting(true);
      try {
        const response = await getIpptaExportable();
        const data = response.data;
        setDataExport(data);
      } catch (error) {
        console.error("Error al obtener datos:", error);
      } finally {
        setIsExporting(false);
      }
    };

    fetchData();
  }, []);


  if (loading) {
    return <Spin fullscreen tip="Cargando..." />;
  }



  const showModal = () => {
    setFormData(null);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setFormData(null);
    setIsModalVisible(false);
  };

  const handleCloseModalRadar = () => {
    setShowRadar(false);
  };

  const filteredData = dataSource.filter(item => {
    return (
      (identificacionFilter === '' || item.identificacion.includes(identificacionFilter)) &&
      (proyectoFilter === '' || item.codigo === proyectoFilter)
    );
  });


  const exportToExcel = async () => {

    if (!dataExport || dataExport.length === 0) {
      console.warn('No hay datos para exportar.');
      return;
    }

    const filteredDataExcel = dataExport.filter(item =>
      (identificacionFilter === '' || item.identificacion.includes(identificacionFilter)) &&
      (proyectoFilter === '' || item.codigo === proyectoFilter)
    );

    if (filteredDataExcel.length === 0) {
      console.warn('No hay datos que coincidan con los filtros.');
      return;
    }

    const updatedData = filteredDataExcel.map(item => {
      const calcularSuma = (prefijo, cantidad) =>
        Array.from({ length: cantidad }, (_, i) => item[`${prefijo}_indicador${i + 1}`] || 0)
          .reduce((acc, curr) => acc + curr, 0);

      const sumaProductivo = calcularSuma('productivo', 7);
      const sumaSocioeconomico = calcularSuma('socioeconomico', 5);
      const sumaAmbiental = calcularSuma('ambiental', 6);

      return {
        ...item,
        porcentajeProductivo: ((sumaProductivo / 35) * 100).toFixed(2) + '%',
        porcentajeSocioeconomico: ((sumaSocioeconomico / 25) * 100).toFixed(2) + '%',
        porcentajeAmbiental: ((sumaAmbiental / 30) * 100).toFixed(2) + '%',
        porcentajeTotal: (((sumaProductivo / 35) + (sumaSocioeconomico / 25) + (sumaAmbiental / 30)) / 3 * 100).toFixed(2) + '%'
      };
    });

    setExportData([...updatedData]);
    setTimeout(() => setIsExporting(true), 100); // Espera para que React actualice el estado
  };

  const handleClearFilters = () => {
    setIdentificacionFilter('');
    setProyectoFilter('');
    setDataSource(originalData);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "¿Eliminar registro?",
      content: `Esta acción eliminará el registro del participante: ${record.participante}`,
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await deleteIppta(record.codigo_ippta, userLogin);
          message.success("Registro eliminado correctamente");
          fetchData();
        } catch (error) {
          message.error("Error eliminando el registro");
        }
      }
    });
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    }, {
      title: 'Acciones',
      key: 'acciones',
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '30px', justifyContent: 'center' }}>
          <Tooltip title="Editar">
            <EditOutlined

              onClick={() => handleEdit(record)}
              style={{ cursor: 'pointer', color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Plan de acción">
            <ProfileOutlined
              onClick={() => handPlanAccion(record)}
              style={{
                cursor: 'pointer',
                color: record.plan_accion === 1 ? 'green' : 'red', // Cambia el color según el valor
              }}
            />
          </Tooltip>

          <Tooltip title="Ver gráfico">
            <RadarChartOutlined
              onClick={() => handleChart(record)}
              style={{ cursor: 'pointer', color: '#1890ff' }}
            />
          </Tooltip>
          {permissionDelete && (
            <Tooltip title="Eliminar">
              <DeleteOutlined
                onClick={() => handleDelete(record)}
                style={{ cursor: 'pointer', color: 'red' }}
              />
            </Tooltip>)}
        </div>
      ),
    },
    {
      title: 'Fecha Registro',
      dataIndex: 'fecha',
      key: 'fecha',
      responsive: ['md'],
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Proyecto',
      dataIndex: 'codigoProyecto',
      key: 'codigoProyecto',
      responsive: ['md'],
      render: (text) => <span>{text}</span>,
    }, {
      title: 'Identificación',
      dataIndex: 'identificacion',
      key: 'identificacion',
      responsive: ['md'],
    },
    {
      title: 'Participante',
      dataIndex: 'participante',
      key: 'participante',
      responsive: ['sm'],
    },
    {
      title: 'Vereda',
      dataIndex: 'vereda',
      key: 'vereda',
      responsive: ['md'],
    },
    {
      title: 'Municipio',
      dataIndex: 'municipio',
      key: 'municipio',
      responsive: ['lg'],
    },
    {
      title: 'Departamento',
      dataIndex: 'departamento',
      key: 'departamento',
      responsive: ['md'],
    },
    {
      title: 'Medición General',
      dataIndex: 'medicionGeneral',
      key: 'medicionGeneral',
      responsive: ['lg'],
      render: (text) => {
        const percent = parseInt(text.replace('%', ''), 10);
        return (
          <Flex vertical gap="middle" style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Progress percent={percent} strokeColor={twoColors} format={percent === 100 ? () => '100%' : () => `${percent}%`} // Muestra el porcentaje
            />
          </Flex>
        );
      },
    }
  ];

  const handleTableChange = (pagination) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleInforme = () => {
    if (proyectoFiltrar) {
      setItemParams({ proyectoEdah: proyectoFiltrar, nuevaRuta: true, rutaInformacionGeneral: "informes_ippta" });
    } else {
      setItemParams({ proyectoEdah: "todos", nuevaRuta: true, rutaInformacionGeneral: "informes_ippta" });
    }

    setOpenModalVisitasEcas(true);
  };

  const hideModalVisitasEcas = () => {
    setOpenModalVisitasEcas(false);
    setItemParams(null); // opcional, limpiar variable
  };

  return (
    <div className="container">
      <React.Fragment>
        <React.Fragment>
          <div className="row mb-2 d-flex align-items-center" >
            <div className="col-md-8 linea_separador mb-2 d-flex align-items-center">
              <div className="titulo_proyecto" style={{ flexBasis: '25%', flexGrow: 0 }}>
                <p>PROYECTO UTF <br /> COL 160 COL</p>
              </div>
              <div className="objeto" style={{ flexBasis: '75%', flexGrow: 0 }}>
                <p>Implementar actividades de desarrollo económico, derecho humano a la alimentación y fortalecimiento de capacidades institucionales, comunitarias y organizacionales que aporten al cierre de brechas y la transformación de los territorios PDET</p>
              </div>
            </div>
            <div className="col-md-4 d-flex justify-content-center align-items-center flex-column">
              <h2 className="text-center mb-2">IPPTA</h2>
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="input-group shadow-sm">
                    {permissionInsert && (
                      <React.Fragment>
                        <Button
                          type="danger"
                          className="btn btn-primary me-2"
                          size="large"
                          icon={<PlusOutlined />}
                          onClick={showModal} // Abre el modal
                        >
                          Nueva
                        </Button>
                        <IpptaForm
                          visible={isModalVisible}
                          onClose={handleCloseModal}
                          formData={formData}
                          loadData={fetchData}
                        />
                      </React.Fragment>
                    )}
                    {permissionExport && (
                      <Button
                        type="danger"
                        className="btn btn-primary me-2"
                        size="large"
                        onClick={exportToExcel}
                        icon={<DownloadOutlined />}
                        disabled={isExporting}
                      >
                        {isExporting ? 'Exportando...' : 'Exportar a Excel'}
                      </Button>
                    )}
                    {isExporting && <IpptaExport data={exportData} onFinish={() => setIsExporting(false)} />}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
            <Col span={8}>
              <Input
                placeholder="Filtrar por Identificación"
                value={identificacionFilter}
                onChange={(e) => setIdentificacionFilter(e.target.value)}
                style={{ height: '40px', padding: '10px' }}
              />
            </Col>
            <Col span={8}  >
              <Select
                style={{ width: '100%', height: '40px' }}
                placeholder="Filtrar por proyecto"
                showSearch
                value={proyectoFilter || undefined}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) => setProyectoFilter(value)}
              >
                {proyectos?.map(proyecto => (
                  <Option key={proyecto.proyecto_oid} value={proyecto.proyecto_codigo}>
                    {`${proyecto.proyecto_codigo} - ${proyecto.nombre_proyecto}`}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Button
                type="default"
                onClick={handleClearFilters}
                icon={<ClearOutlined />}
                style={{ height: '40px', width: "100%" }}
              >
                Limpiar
              </Button>
            </Col>
            <Col span={4}>
              <Button
                type="primary"
                icon={<FolderOpenOutlined />}
                style={{ height: "40px", width: "100%" }}
                onClick={handleInforme}
              >
                Informe
              </Button>
            </Col>
          </Row>
          <div className="participant-count-container">
            <h1 className="participant-count-title">
              Número de Registros: <span className="participant-count-number">{filteredData.length}</span>
            </h1>
          </div>
          <div>
            <Col span={24}>
              <Table
                className="custom-ippta-table"
                dataSource={filteredData}
                columns={columns}
                rowKey="codigo_ippta"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                }}
                onChange={handleTableChange}
              />
            </Col>
          </div>
        </React.Fragment>
      </React.Fragment>
      <Modal
        title={<h3 style={{ textAlign: 'center', fontWeight: 'bold' }}>Gráfico IPPTA</h3>}
        open={showRadar}
        width={1200}
        styles={{ body: { height: '1100px' } }}
        onCancel={handleCloseModalRadar}
        footer={null}
      >
        <IpptaRadar formData={identificacionGrafica} />
      </Modal>
      <Modal
        title={<h3 style={{ textAlign: 'center', fontWeight: 'bold' }}>PLAN DE ACCIÓN PREDIAL PARA LA TRANSICIÓN AGROECOLÓGICA</h3>}
        open={showPlanAccion}
        width={1200}
        onCancel={handleCloseModalPlanAccion}
        footer={null}
      >
        <PlanAccion oidRegistro={oidRegistro} />
      </Modal>
      {openModalVisitasEcas && (
        <Modal
          title={<span className="custom-modal-title">Medios de verificación</span>}
          open={openModalVisitasEcas}
          onCancel={hideModalVisitasEcas}
          footer={null}
          destroyOnHidden
          width="95%"
          style={{ top: 20 }}
        >
          <FileManagerPOA
            codigo_tarea={{ codigo_tarea: itemParams }}
            onClose={hideModalVisitasEcas}
          />
        </Modal>
      )}
    </div>
  );
};

export default Ippta;
